import { datadogRum } from '@datadog/browser-rum';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewVizzleIframe.scss';
import { useDomain } from '../../../states/AppDomainProvider';
import { getUserToken, saveTokenToMemory } from '../../../utils/VizzleSessionStorage';
import { dispatchSessionTimeoutEvent } from '../../../services/VizzleEvents';
import LogoutConfirmation from '../../../containers/logout/LogoutConfirmation';
import { useLogout } from '../../../containers/logout/LogoutHooks';
import { logout } from '../../../services/LoginService';
import LiveChatService from '../../../services/LiveChatService';
import { registerMedia } from '../../../services/MediaRegistrationService';
import { filterUndefined } from '../../../utils/FilterUndefinedObject';

const t = new Date().getTime();

const NewVizzleIframe = forwardRef(({
  ...props
}, ref) => {
  const defaultProps = {
    payload: {},
  };
  const {
    path,
    payload,
  } = { ...defaultProps, ...filterUndefined(props) };
  const { userDomain, studentsDomain } = useDomain();
  const { user, userProfile, terminologies } = userDomain.domainData;
  const { students } = studentsDomain.domainData;
  const navigate = useNavigate();

  const iframeRef = useRef();

  useImperativeHandle(ref, () => ({
    postMessageToIFrame: (message, data) => {
      iframeRef.current?.contentWindow?.postMessage({
        event: message,
        data,
      }, '*');
    },
  }));

  const {
    logoutDialog,
    openLogoutDialog,
    closeLogoutDialog,
  } = useLogout();

  useEffect(() => {
    const register = (e) => {
      const { data } = e;
      if (data.event === 'page-ready') {
        iframeRef.current?.contentWindow?.postMessage({
          event: 'initialization',
          data: {
            path,
            payload,
            userToken: getUserToken(),
            userInfo: {
              user,
              userProfile,
              terminologies,
              students,
            },
          },
        }, '*');

        const context = datadogRum.getInternalContext();
        // iframeRef.current?.contentWindow?.postMessage({ type: 'DATADOG_CONTEXT', context }, process.env.REACT_APP_VIZZLE_REACT.replace('https://', ''));
        iframeRef.current?.contentWindow?.postMessage({ type: 'DATADOG_CONTEXT', context }, process.env.REACT_APP_VIZZLE_REACT);
      } else if (data.event === 'update-token') {
        saveTokenToMemory(data.data.token);
      } else if (data.event === 'token-expired') {
        dispatchSessionTimeoutEvent('Your session expired please log in again.');
      } else if (data.event === 'hide-toolbar') {
        const toolbar = document.querySelector('.vizzle-app-bar-position-component');
        if (toolbar) {
          toolbar.style.display = 'none';
        }
      } else if (data.event === 'show-toolbar') {
        const toolbar = document.querySelector('.vizzle-app-bar-position-component');
        if (toolbar) {
          toolbar.style.display = null;
        }
      } else if (data.event === 'update-user-profile') {
        userDomain.updateUserProfile(data.data);
      } else if (data.event === 'update-terminologies') {
        userDomain.updateTerminologies(data.data);
      } else if (data.event === 'logout') {
        if (data.data.showDialog) {
          openLogoutDialog();
        } else {
          logout();
        }
      } else if (data.event === 'show-chat-icon') {
        const { show } = data.data;
        if (show) {
          // Check if lesson-play-dialog exists, if so hide the live chat icon
          const lessonPlayDialog = document.querySelector('.lesson-play-dialog');
          if (lessonPlayDialog) {
            LiveChatService.hideLiveChatIcon();
          } else {
            LiveChatService.showLiveChatIcon();
          }
        } else {
          LiveChatService.hideLiveChatIcon();
        }
      } else if (data.event === 'register-media') {
        registerMedia();
      } else if (data.event === 'redirect-to') {
        navigate(data.data.path);
      }
    };
    window.addEventListener('message', register);

    return () => {
      window.removeEventListener('message', register);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <iframe
        ref={iframeRef}
        className='new-vizzle-iframe'
        data-test='new-vizzle-iframe'
        src={`${process.env.REACT_APP_VIZZLE_REACT}/internal-login?t=${t}`}
        title='Vizzle iframe'
        // eslint-disable-next-line
        tabIndex={0}
      />
      <LogoutConfirmation
        open={logoutDialog}
        onClose={closeLogoutDialog}
      />
    </>
  );
});

export default NewVizzleIframe;
